import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Download, Calendar, Briefcase, Award } from 'lucide-react';
import { portfolioData } from '../data/mock';
import { Reveal } from './Reveal';
import { CoverMedia } from './CoverMedia';

const HERO_TEXTURE =
  'https://images.unsplash.com/photo-1634648852208-fc7e55a15751?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxzaWx2ZXIlMjBtZXRhbGxpYyUyMHRleHR1cmV8ZW58MHx8fHwxNzgxNzE5ODE0fDA&ixlib=rb-4.1.0&q=85';

export const Hero = () => {
  const { personal } = portfolioData;
  return (
    <section className="relative pt-40 pb-28 px-6 lg:px-12 min-h-[88vh] flex items-center">
      <div className="absolute inset-0 z-0 onyx-mesh" />
      <div
        className="absolute inset-0 z-0 opacity-[0.07] mix-blend-screen bg-cover bg-center animate-float"
        style={{ backgroundImage: `url(${HERO_TEXTURE})` }}
      />
      <div className="absolute inset-x-0 bottom-0 h-40 z-0 bg-gradient-to-t from-[#050505] to-transparent" />

      <div className="max-w-7xl mx-auto relative z-10 w-full">
        <div className="max-w-5xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass inline-block text-xs font-medium text-zinc-300 uppercase tracking-[0.3em] px-5 py-2.5 rounded-full mb-10"
            data-testid="hero-badge"
          >
            {personal.name} — {personal.title}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading font-extrabold tracking-tight text-5xl md:text-7xl lg:text-8xl leading-[0.98] mb-10 silver-text-animate"
            data-testid="hero-headline"
          >
            Simplicity is the<br />ultimate sophistication.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-zinc-400 mb-12 max-w-2xl mx-auto font-light leading-relaxed"
          >
            Designing intentional interfaces that cut through the noise and solve real problems for real people.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="flex flex-wrap items-center justify-center gap-4"
          >
            <a
              href="#work"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 bg-white text-black px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-zinc-200 hover:scale-[1.03] transition-all duration-300"
            >
              Explore Case Studies
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={personal.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-resume-button"
              className="glass-btn inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium text-white"
            >
              <Download size={17} />
              Resume
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const Statement = () => {
  const { personal } = portfolioData;
  return (
    <section className="py-20 px-6 lg:px-12 border-y border-white/5">
      <div className="max-w-5xl mx-auto">
        <Reveal as="p" className="text-3xl md:text-5xl font-heading font-bold text-center tracking-tight silver-text">
          {personal.subtitle}
        </Reveal>
      </div>
    </section>
  );
};

export const SelectedProjects = () => (
  <section id="work" className="py-28 md:py-36 px-6 lg:px-12">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-16 text-center">
        <span className="text-xs text-zinc-500 uppercase tracking-[0.3em]">Portfolio</span>
        <h2 className="text-4xl md:text-6xl font-heading font-bold mt-4 silver-text">Selected Projects</h2>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
        {portfolioData.caseStudies.map((project, index) => (
          <Reveal key={project.id} delay={index * 0.08}>
            <Link
              to={`/case-study/${project.id}`}
              data-testid={`project-card-${project.id}`}
              className="group block glass glass-hover rounded-3xl overflow-hidden h-full"
            >
              <div className="aspect-[16/11] overflow-hidden relative bg-[#0A0A0C]">
                <CoverMedia
                  item={project}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 brightness-90 group-hover:brightness-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 right-4 glass-btn opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium text-white">
                  View Case Study <ArrowRight size={14} />
                </div>
              </div>
              <div className="p-7">
                <span className="text-xs text-zinc-500 uppercase tracking-[0.2em]">{project.company}</span>
                <h3 className="text-2xl md:text-3xl font-heading font-bold mt-3 mb-3 text-white group-hover:silver-text transition-all">
                  {project.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {project.services.slice(0, 3).map((service) => (
                    <span key={service} className="text-[11px] font-medium px-3 py-1 rounded-full border border-white/10 text-zinc-400">
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const About = () => {
  const { personal } = portfolioData;
  return (
    <section id="about" className="py-28 md:py-36 px-6 lg:px-12 border-t border-white/5">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <Reveal className="order-2 lg:order-1">
          <span className="text-xs text-zinc-500 uppercase tracking-[0.3em]">About</span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold mt-4 mb-6 silver-text">About Me</h2>
          <p className="text-lg text-zinc-400 mb-10 font-light leading-relaxed">{personal.bio}</p>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="glass glass-hover rounded-2xl p-6">
              <Briefcase size={22} className="text-zinc-400 mb-3" />
              <p className="text-sm text-zinc-500 mb-1">Experience</p>
              <p className="text-xl font-heading font-bold text-white">{personal.yearsOfExperience}</p>
            </div>
            <div className="glass glass-hover rounded-2xl p-6">
              <Award size={22} className="text-zinc-400 mb-3" />
              <p className="text-sm text-zinc-500 mb-1">Projects</p>
              <p className="text-xl font-heading font-bold text-white">{portfolioData.metrics[0].value}+ Completed</p>
            </div>
          </div>

          <div className="space-y-3">
            {portfolioData.companies.map((company) => (
              <div key={company.name} className="glass rounded-xl px-5 py-4 flex items-center">
                <span className="w-2 h-2 rounded-full bg-zinc-400 mr-4" />
                <span className="text-zinc-300 text-sm font-medium">
                  {company.name} — {company.role}
                  {company.current && (
                    <span className="ml-3 text-[10px] uppercase tracking-wider text-zinc-500 border border-white/10 rounded-full px-2 py-0.5">Current</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="order-1 lg:order-2">
          <div className="relative">
            <img
              src={personal.image}
              alt={personal.name}
              className="relative w-full rounded-3xl grayscale-[15%]"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const Skills = () => (
  <section className="py-28 md:py-36 px-6 lg:px-12">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-16 text-center">
        <span className="text-xs text-zinc-500 uppercase tracking-[0.3em]">Capabilities</span>
        <h2 className="text-4xl md:text-5xl font-heading font-bold mt-4 silver-text">Core Skills</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {portfolioData.skills.map((skill, idx) => (
          <Reveal key={skill.title} delay={idx * 0.06}>
            <div className="glass glass-hover rounded-2xl p-7 h-full">
              <div className="flex items-center mb-4">
                <span className="w-10 h-10 rounded-xl glass-btn flex items-center justify-center font-heading font-bold text-zinc-300 mr-3">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <h3 className="text-lg font-heading font-bold text-white">{skill.title}</h3>
              </div>
              <p className="text-sm text-zinc-400 font-light leading-relaxed">{skill.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Expertise = () => {
  const { personal } = portfolioData;
  return (
    <section id="expertise" className="py-28 md:py-36 px-6 lg:px-12 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <Reveal className="mb-16 text-center">
          <span className="text-xs text-zinc-500 uppercase tracking-[0.3em]">Expertise</span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold mt-4 mb-6 silver-text">Creative Expertise</h2>
          <p className="text-lg text-zinc-400 italic max-w-2xl mx-auto font-light">"{personal.philosophy}"</p>
        </Reveal>
        <div className="space-y-5">
          {portfolioData.expertise.map((item, idx) => (
            <Reveal key={item.number} delay={idx * 0.06}>
              <div className="glass glass-hover rounded-3xl p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-1">
                  <span className="text-4xl font-heading font-extrabold silver-text">{item.number}</span>
                </div>
                <div className="lg:col-span-11">
                  <h3 className="text-2xl font-heading font-bold mb-3 text-white">{item.title}</h3>
                  <p className="text-zinc-400 mb-5 max-w-3xl font-light leading-relaxed">{item.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.services.map((service) => (
                      <span key={service} className="text-xs font-medium px-4 py-1.5 rounded-full border border-white/10 text-zinc-400">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Tools = () => (
  <section className="py-28 md:py-36 px-6 lg:px-12">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-16 text-center">
        <span className="text-xs text-zinc-500 uppercase tracking-[0.3em]">Stack</span>
        <h2 className="text-4xl md:text-5xl font-heading font-bold mt-4 silver-text">Tools I Use</h2>
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {portfolioData.tools.map((tool, idx) => (
          <Reveal key={tool.name} delay={idx * 0.05}>
            <div className="glass glass-hover rounded-2xl p-6 text-center h-full">
              <div className="text-2xl font-heading font-bold mb-1 text-white">{tool.name}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">{tool.category}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Contact = () => {
  const { personal } = portfolioData;
  return (
    <section id="contact" className="py-32 md:py-44 px-6 lg:px-12 border-t border-white/5 relative">
      <div className="absolute inset-0 onyx-mesh opacity-60" />
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <Reveal>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-heading font-extrabold tracking-tight silver-text mb-8">
            Let's Talk
          </h2>
          <p className="text-lg md:text-xl text-zinc-400 mb-12 font-light">
            Have a project in mind? Available for new opportunities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={personal.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-book-call"
              className="group inline-flex items-center gap-2 bg-white text-black px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-zinc-200 hover:scale-[1.03] transition-all duration-300"
            >
              <Calendar size={17} />
              Book a Free Call
            </a>
            <a
              href={`mailto:${personal.email}`}
              data-testid="contact-email"
              className="glass-btn inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium text-white"
            >
              Email Me
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
